/**
 * Pfitzinger Marathon Pace Calculator Utilities
 * Calculates training pace zones and heart rate zones based on goal marathon time and age.
 */

// Marathon distance in miles
const MARATHON_DISTANCE = 26.2188;

// Consistent run type order (same for both pace and HR)
export const RUN_TYPE_ORDER = [
  { key: 'LR', name: 'Long Run', abbrev: 'LR' },
  { key: 'MP', name: 'Marathon Pace', abbrev: 'MP' },
  { key: 'GA', name: 'General Aerobic', abbrev: 'GA' },
  { key: 'Rec', name: 'Recovery', abbrev: 'Rec' },
  { key: 'LT', name: 'Lactate Threshold', abbrev: 'LT' },
  { key: 'HM', name: 'Half Marathon', abbrev: 'HM' },
  { key: 'VO2', name: 'VO2max', abbrev: 'VO2' }
];

// Pace zone multipliers (higher multiplier = slower pace)
const PACE_ZONE_CONFIG = {
  LR: { min: 1.10, max: 1.20 },
  MP: { min: 1.00, max: 1.00 },
  GA: { min: 1.15, max: 1.25 },
  Rec: { min: 1.30, max: null }, // No upper limit
  LT: { min: 0.91, max: 0.94 },
  HM: { min: 0.95, max: 0.98 },
  VO2: { min: null, max: null } // As fast as possible
};

// HR zone multipliers (% of max HR)
const HR_ZONE_CONFIG = {
  LR: { min: 0.74, max: 0.84 },
  MP: { min: 0.79, max: 0.88 },
  GA: { min: 0.70, max: 0.81 },
  Rec: { min: null, max: 0.76 }, // Do not exceed
  LT: { min: 0.82, max: 0.91 },
  HM: { min: null, max: null }, // N/A
  VO2: { min: 0.93, max: 0.95 }
};

/**
 * Parse marathon time string to seconds
 * @param {string} timeString - Time in H:MM:SS or HH:MM:SS format
 * @returns {number|null} Time in seconds or null if invalid
 */
export function parseMarathonTime(timeString) {
  if (!timeString || typeof timeString !== 'string') return null;

  const trimmed = timeString.trim();
  const parts = trimmed.split(':');

  if (parts.length !== 3) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
  if (hours < 0 || minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) return null;

  return (hours * 3600) + (minutes * 60) + seconds;
}

/**
 * Format seconds to H:MM:SS string
 * @param {number} totalSeconds - Total seconds
 * @returns {string} Formatted time string
 */
export function formatMarathonTime(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate goal pace per mile from marathon time
 * @param {number} marathonSeconds - Marathon time in seconds
 * @returns {number} Pace in seconds per mile
 */
export function calculateGoalPace(marathonSeconds) {
  if (!marathonSeconds || marathonSeconds <= 0) return null;
  return marathonSeconds / MARATHON_DISTANCE;
}

/**
 * Format pace seconds to MM:SS string
 * @param {number} paceSeconds - Pace in seconds per mile
 * @returns {string} Formatted pace string (e.g., "8:00")
 */
export function formatPaceMinSec(paceSeconds) {
  if (paceSeconds === null || paceSeconds === undefined || isNaN(paceSeconds)) return 'N/A';

  const minutes = Math.floor(paceSeconds / 60);
  const seconds = Math.round(paceSeconds % 60);

  // Handle case where seconds rounds to 60
  if (seconds === 60) {
    return `${minutes + 1}:00`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate all pace zones from goal pace
 * @param {number} goalPaceSeconds - Goal pace in seconds per mile
 * @returns {Object} Pace zones with min/max values
 */
export function calculatePaceZones(goalPaceSeconds) {
  if (!goalPaceSeconds || goalPaceSeconds <= 0) return null;

  const zones = {};

  RUN_TYPE_ORDER.forEach(({ key }) => {
    const config = PACE_ZONE_CONFIG[key];

    if (key === 'VO2') {
      // VO2 is "as fast as possible"
      zones[key] = {
        min: null,
        max: null,
        display: 'As Fast As Possible'
      };
    } else if (config.max === null) {
      // Recovery: slower than threshold
      const minPace = goalPaceSeconds * config.min;
      zones[key] = {
        min: minPace,
        max: null,
        display: `> ${formatPaceMinSec(minPace)}/mi`
      };
    } else {
      // Normal range
      const minPace = goalPaceSeconds * config.min;
      const maxPace = goalPaceSeconds * config.max;
      zones[key] = {
        min: minPace,
        max: maxPace,
        display: config.min === config.max
          ? `${formatPaceMinSec(minPace)}/mi`
          : `${formatPaceMinSec(minPace)} - ${formatPaceMinSec(maxPace)}/mi`
      };
    }
  });

  return zones;
}

/**
 * Calculate max heart rate from age using 220 - age formula
 * @param {number} age - Age in years
 * @returns {number} Max heart rate in bpm
 */
export function calculateMaxHR(age) {
  if (!age || age <= 0 || age > 120) return null;
  return 220 - age;
}

/**
 * Calculate all HR zones from max heart rate
 * @param {number} maxHR - Maximum heart rate in bpm
 * @returns {Object} HR zones with min/max values
 */
export function calculateHRZones(maxHR) {
  if (!maxHR || maxHR <= 0) return null;

  const zones = {};

  RUN_TYPE_ORDER.forEach(({ key }) => {
    const config = HR_ZONE_CONFIG[key];

    if (key === 'HM') {
      // HM doesn't have HR zones in Pfitzinger
      zones[key] = {
        min: null,
        max: null,
        display: 'N/A'
      };
    } else if (config.min === null && config.max !== null) {
      // Recovery: do not exceed
      const maxBPM = Math.round(maxHR * config.max);
      zones[key] = {
        min: null,
        max: maxBPM,
        display: `< ${maxBPM} bpm`
      };
    } else if (config.min !== null && config.max !== null) {
      // Normal range
      const minBPM = Math.round(maxHR * config.min);
      const maxBPM = Math.round(maxHR * config.max);
      zones[key] = {
        min: minBPM,
        max: maxBPM,
        display: `${minBPM} - ${maxBPM} bpm`
      };
    } else {
      // Shouldn't happen, but handle gracefully
      zones[key] = {
        min: null,
        max: null,
        display: 'N/A'
      };
    }
  });

  return zones;
}

/**
 * Calculate all zones for a given goal time and age
 * @param {string} goalTimeString - Goal marathon time in H:MM:SS format
 * @param {number} age - Age in years
 * @returns {Object} Complete calculation results
 */
export function calculateAllZones(goalTimeString, age) {
  const marathonSeconds = parseMarathonTime(goalTimeString);
  if (!marathonSeconds) {
    return { error: 'Invalid marathon time format. Use H:MM:SS (e.g., 3:30:00)' };
  }

  const ageNum = parseInt(age, 10);
  if (!ageNum || ageNum < 10 || ageNum > 100) {
    return { error: 'Invalid age. Enter a number between 10 and 100.' };
  }

  const goalPace = calculateGoalPace(marathonSeconds);
  const maxHR = calculateMaxHR(ageNum);
  const paceZones = calculatePaceZones(goalPace);
  const hrZones = calculateHRZones(maxHR);

  return {
    inputs: {
      goalTimeString,
      goalTimeSeconds: marathonSeconds,
      age: ageNum
    },
    goalPace: {
      secondsPerMile: goalPace,
      display: formatPaceMinSec(goalPace) + '/mi'
    },
    maxHR,
    paceZones,
    hrZones
  };
}
